import React, { useMemo } from 'react';
import { MapIcon, ArrowRightIcon, ArrowDownIcon, DatabaseIcon, WebhookIcon, CreditCardIcon, BotIcon, ServerIcon, CodeIcon } from 'lucide-react';
import { getWizardState, getRecommendation } from '../lib/wizardUtils';
import { InfraPanel, InfraItem } from '../components/ui/InfraPanel';
import { Terminal } from '../components/ui/Terminal';
import { StatusBadge } from '../components/ui/StatusBadge';
export function BlueprintPage() {
  const wizardState = useMemo(() => getWizardState(), []);
  const rec = useMemo(() => getRecommendation(wizardState), [wizardState]);
  const infraItems: InfraItem[] = useMemo(() => {
    const items: InfraItem[] = [];
    rec.stripeObjects.forEach(obj => {
      if (obj.type === 'product' || obj.type === 'price') {
        items.push({
          label: obj.name,
          value: obj.details || 'Pricing configuration',
          status: 'success',
          stripeId: obj.id,
          type: 'payment'
        });
      } else if (obj.type === 'webhook') {
        items.push({
          label: 'Webhook Endpoint',
          value: obj.details || 'Listening to events',
          status: 'success',
          stripeId: obj.id,
          type: 'webhook'
        });
      } else if (obj.type === 'portal') {
        items.push({
          label: 'Customer Portal',
          value: 'Self-serve billing UI',
          status: 'success',
          stripeId: obj.id,
          type: 'module'
        });
      }
    });
    items.push({
      label: 'Database Schema',
      value: `${rec.tables.length} tables configured`,
      status: 'success',
      type: 'database'
    });
    return items;
  }, [rec]);
  const terminalCommands = [{
    input: 'npx paykit monetize',
    output: ['✔ Authenticated with Stripe (acct_1N2M...)', '✔ Analyzed local Next.js project', '✔ Provisioned 3 Products and 5 Prices', '✔ Configured Webhook Endpoint (we_1N2M...)', '✔ Generated database migrations', '✔ Injected API routes and frontend components', '', '✨ Monetization stack ready! Run `npm run dev` to test.'],
    delay: 4000
  }];
  return <div className="flex flex-col pb-12" data-id="element-379">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4" data-id="element-380">
        <div data-id="element-381">
          <h1 className="text-3xl font-bold mb-1 flex items-center gap-2" data-id="element-382">
            <MapIcon className="w-8 h-8 text-primary" data-id="element-383" />
            Monetization Blueprint
          </h1>
          <p className="text-muted-foreground" data-id="element-384">
            Architecture overview and infrastructure transparency for your{' '}
            {wizardState.buildingType}.
          </p>
        </div>
        <div className="flex items-center gap-3" data-id="element-385">
          <StatusBadge status="success" label="Architecture Synced" data-id="element-386" />
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-8 mb-8" data-id="element-387">
        {/* Left Column: Architecture & Flow */}
        <div className="lg:col-span-2 space-y-8" data-id="element-388">
          {/* Visual Flow Diagram */}
          <section className="bg-card border border-border rounded-xl p-6 shadow-sm" data-id="element-389">
            <h2 className="text-lg font-bold mb-6" data-id="element-390">Payment Lifecycle Flow</h2>
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 p-4 bg-pk-surface rounded-lg border border-border" data-id="element-391">
              <div className="flex flex-col items-center text-center w-full md:w-1/4" data-id="element-392">
                <div className="w-12 h-12 bg-primary/10 text-primary rounded-lg flex items-center justify-center mb-3 border border-primary/20" data-id="element-393">
                  <CreditCardIcon className="w-6 h-6" data-id="element-394" />
                </div>
                <div className="font-mono text-sm font-semibold" data-id="element-395">Checkout</div>
                <div className="text-xs text-muted-foreground mt-1" data-id="element-396">
                  Stripe Hosted UI
                </div>
              </div>

              <ArrowRightIcon className="hidden md:block w-6 h-6 text-muted-foreground shrink-0" data-id="element-397" />
              <ArrowDownIcon className="md:hidden w-6 h-6 text-muted-foreground shrink-0" data-id="element-398" />

              <div className="flex flex-col items-center text-center w-full md:w-1/4" data-id="element-399">
                <div className="w-12 h-12 bg-pk-info/10 text-pk-info rounded-lg flex items-center justify-center mb-3 border border-pk-info/20" data-id="element-400">
                  <WebhookIcon className="w-6 h-6" data-id="element-401" />
                </div>
                <div className="font-mono text-sm font-semibold" data-id="element-402">Webhook</div>
                <div className="text-xs text-muted-foreground mt-1" data-id="element-403">
                  {rec.events.length} Events
                </div>
              </div>

              <ArrowRightIcon className="hidden md:block w-6 h-6 text-muted-foreground shrink-0" data-id="element-404" />
              <ArrowDownIcon className="md:hidden w-6 h-6 text-muted-foreground shrink-0" data-id="element-405" />

              <div className="flex flex-col items-center text-center w-full md:w-1/4" data-id="element-406">
                <div className="w-12 h-12 bg-pk-success/10 text-pk-success rounded-lg flex items-center justify-center mb-3 border border-pk-success/20" data-id="element-407">
                  <DatabaseIcon className="w-6 h-6" data-id="element-408" />
                </div>
                <div className="font-mono text-sm font-semibold" data-id="element-409">
                  {rec.dbLabel}
                </div>
                <div className="text-xs text-muted-foreground mt-1" data-id="element-410">
                  {rec.tables.length} Tables Sync
                </div>
              </div>
            </div>
          </section>

          {/* MCP Integration Diagram */}
          <section className="bg-card border border-border rounded-xl p-6 shadow-sm" data-id="element-411">
            <h2 className="text-lg font-bold mb-2" data-id="element-412">MCP Integration</h2>
            <p className="text-sm text-muted-foreground mb-6" data-id="element-413">
              How PayKit connects to your AI coding agents via the Model Context
              Protocol.
            </p>

            <div className="relative p-6 bg-[#0d1117] rounded-lg border border-pk-code-border overflow-hidden" data-id="element-414">
              {/* Grid background effect */}
              <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]" data-id="element-415"></div>

              <div className="relative z-10 flex flex-col md:flex-row items-center justify-center gap-6" data-id="element-416">
                <div className="bg-card border border-border rounded-lg p-4 w-40 text-center shadow-lg" data-id="element-417">
                  <BotIcon className="w-8 h-8 mx-auto mb-2 text-foreground" data-id="element-418" />
                  <div className="font-semibold text-sm" data-id="element-419">AI Agent</div>
                  <div className="text-[10px] text-muted-foreground mt-1" data-id="element-420">
                    Cursor / Windsurf
                  </div>
                </div>

                <div className="flex flex-col items-center" data-id="element-421">
                  <div className="text-[10px] font-mono text-pk-teal mb-1" data-id="element-422">
                    MCP Protocol
                  </div>
                  <div className="flex items-center gap-1" data-id="element-423">
                    <div className="w-2 h-2 rounded-full bg-pk-teal animate-pulse" data-id="element-424"></div>
                    <div className="h-px w-12 bg-pk-teal border-t border-dashed border-pk-teal" data-id="element-425"></div>
                    <ArrowRightIcon className="w-4 h-4 text-pk-teal" data-id="element-426" />
                  </div>
                </div>

                <div className="bg-primary/10 border border-primary/30 rounded-lg p-4 w-40 text-center shadow-lg shadow-primary/5" data-id="element-427">
                  <ServerIcon className="w-8 h-8 mx-auto mb-2 text-primary" data-id="element-428" />
                  <div className="font-semibold text-sm text-primary" data-id="element-429">
                    PayKit MCP
                  </div>
                  <div className="text-[10px] text-primary/70 mt-1" data-id="element-430">
                    Context & Tools
                  </div>
                </div>

                <div className="flex flex-col items-center" data-id="element-431">
                  <div className="text-[10px] font-mono text-pk-indigo mb-1" data-id="element-432">
                    REST API
                  </div>
                  <div className="flex items-center gap-1" data-id="element-433">
                    <div className="h-px w-12 bg-pk-indigo" data-id="element-434"></div>
                    <ArrowRightIcon className="w-4 h-4 text-pk-indigo" data-id="element-435" />
                  </div>
                </div>

                <div className="bg-[#635BFF]/10 border border-[#635BFF]/30 rounded-lg p-4 w-40 text-center shadow-lg" data-id="element-436">
                  <CodeIcon className="w-8 h-8 mx-auto mb-2 text-[#635BFF]" data-id="element-437" />
                  <div className="font-semibold text-sm text-[#635BFF]" data-id="element-438">
                    Stripe
                  </div>
                  <div className="text-[10px] text-[#635BFF]/70 mt-1" data-id="element-439">
                    Infrastructure
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Infrastructure Panel */}
          <section data-id="element-440">
            <h2 className="text-lg font-bold mb-4" data-id="element-441">Provisioned Resources</h2>
            <InfraPanel items={infraItems} data-id="element-442" />
          </section>
        </div>

        {/* Right Column: Terminal & Status */}
        <div className="space-y-8" data-id="element-443">
          <section data-id="element-444">
            <h2 className="text-lg font-bold mb-4" data-id="element-445">CLI Integration</h2>
            <Terminal commands={terminalCommands} data-id="element-446" />
            <p className="text-xs text-muted-foreground mt-3" data-id="element-447">
              Use the PayKit CLI to scaffold your monetization stack directly
              from your terminal.
            </p>
          </section>

          <section className="bg-card border border-border rounded-xl p-6 shadow-sm" data-id="element-448">
            <h2 className="text-lg font-bold mb-4" data-id="element-449">Provider Status</h2>

            <div className="space-y-4" data-id="element-450">
              <div className="p-4 border border-pk-success/30 bg-pk-success/5 rounded-lg" data-id="element-451">
                <div className="flex items-center justify-between mb-2" data-id="element-452">
                  <div className="flex items-center gap-2" data-id="element-453">
                    <div className="w-6 h-6 bg-[#635BFF] rounded flex items-center justify-center text-white font-bold text-[10px]" data-id="element-454">
                      S
                    </div>
                    <span className="font-semibold text-sm" data-id="element-455">Stripe</span>
                  </div>
                  <StatusBadge status="success" label="Connected" data-id="element-456" />
                </div>
                <div className="text-xs text-muted-foreground space-y-1" data-id="element-457">
                  <div className="flex justify-between" data-id="element-458">
                    <span data-id="element-459">Account ID</span>
                    <span className="font-mono" data-id="element-460">
                      {wizardState.stripeAccountId || 'acct_1N2M3L4K'}
                    </span>
                  </div>
                  <div className="flex justify-between" data-id="element-461">
                    <span data-id="element-462">API Version</span>
                    <span className="font-mono" data-id="element-463">2023-10-16</span>
                  </div>
                  <div className="flex justify-between" data-id="element-464">
                    <span data-id="element-465">Environment</span>
                    <span className="font-mono text-pk-amber" data-id="element-466">Test Mode</span>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>;
}